import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import {
  AggregatedTournamentModel,
  RewardModel,
  RewardModelWithoutId,
  BetEventModel,
  BetOutcomeModel,
  PaginationResponse,
  PaginationArgs,
  VipTaskModel,
  VipTaskItemModel,
  VipLevelModel,
  VipLevelRewardModel,
  ReferralRewardSettingsModel,
  ReferralRewardUserProgressModel,
  TelegramChatModel,
  TelegramChatMessageModel,
  VipWheelSettingsModel,
  VipTaskUserItemModel,
} from '@shared/types';
import { exportFile } from '@shared/utils';

import {
  GetUsersArgs,
  UpdateBetEventArgs,
  UpdateBetOutcomeArgs,
  SetIsActiveBetEventArgs,
  FinishBetEventArgs,
  FinishBetEventResponse,
  SetBetEventStatusArgs,
  ExportBetOutcomeArgs,
  DeleteBetEventOutcomeArgs,
  UnfinishBetEventResponse,
  UpdateBetEventsOrderArgs,
  UpdateVipTaskArgs,
  UpdateVipTaskItemArgs,
  UpdateVipTasksOrderArgs,
  ValidateVipTaskItemArgs,
  UpdateVipLevelArgs,
  UpdateVipLevelRewardArgs,
  UpdateVipLevelRewardsOrderArgs,
  UpdateRewardArgs,
  ReferralRewardSettingsUpdateArgs,
  UpdateReferralUserRewardChanceArgs,
  GetVipTaskItemsParams,
  DoneVipTaskItemArgs,
  SendAdminMessageArgs,
  UpdateTelegramChatStatusArgs,
  UpdateTelegramStatusArgs,
  AssignUserArgs,
  ExportTopBetsWinStreaksArgs,
  UpdateTicketArgs,
  GetUsersByNicknameParams,
  GetTournamentsArgs,
  SetDefaultContestTicketArgs,
} from './types';

import { localStorageService } from 'v2/api/localStorageService';
import { EnvConfig } from 'v2/config';
import { Geo, Locale, LocalStorageKey } from 'v2/constants';
import { MasterKeyModel } from 'v2/types/MasterKeyModel';
import { SlotModel } from 'v2/types/SlotModel';
import { TicketModel } from 'v2/types/TicketModel';
import { UserModel } from 'v2/types/UserModel';
import { getFileNameFromDispositionHeader } from 'v2/utils';

const baseQuery = fetchBaseQuery({
  credentials: 'include',
  mode: 'cors',
  baseUrl: EnvConfig.CORE_API_CONTEXT,
  prepareHeaders: (headers) => {
    const locale = localStorageService.get<Locale>(LocalStorageKey.LOCALE);
    const geo = localStorageService.get<Geo>(LocalStorageKey.GEO) ?? Geo.RU;

    headers.set('Content-Language', locale ?? Locale.ru_RU);
    headers.set('x-geo', geo);

    return headers;
  },
});

export const dataApi = createApi({
  reducerPath: 'dataApi',
  baseQuery,
  endpoints: (builder) => ({
    getUsers: builder.query<{ result: UserModel[]; total: number }, GetUsersArgs>({
      query: ({ page, limit, filter }) => ({
        url: '/v1/admin/user',
        params: {
          page,
          limit,
          filter,
        },
        method: 'GET',
      }),
    }),
    getUsersByNickname: builder.query<PaginationResponse<UserModel>, GetUsersByNicknameParams>({
      query: ({ nicknames, isStrictSearch }) => ({
        url: `/v1/admin/user/search`,
        params: {
          searchValue: nicknames.join(','),
          isStrictSearch,
        },
        method: 'GET',
      }),
    }),
    resetCompletedDailyTasks: builder.mutation<void, void>({
      query: () => ({
        url: '/v1/admin/user/reset/tasks',
        method: 'PATCH',
      }),
    }),
    resetSpecialDropDates: builder.mutation<void, void>({
      query: () => ({
        url: '/v1/admin/user/reset/special-drop',
        method: 'PATCH',
      }),
    }),
    resetReceivedRewards: builder.mutation<void, void>({
      query: () => ({
        url: '/v1/admin/user/reset/rewards',
        method: 'PATCH',
      }),
    }),
    resetDailyRewardVisits: builder.mutation<void, void>({
      query: () => ({
        url: '/v1/admin/user/reset/visits',
        method: 'PATCH',
      }),
    }),
    getUserById: builder.query<UserModel, string>({
      query: (userId) => ({
        url: `/v1/admin/user/${userId}`,
        method: 'GET',
      }),
    }),
    createReward: builder.mutation<RewardModel, RewardModelWithoutId>({
      query: (payload) => ({
        url: '/admin/user-reward',
        body: payload,
        method: 'POST',
      }),
    }),
    updateReward: builder.mutation<RewardModel, UpdateRewardArgs>({
      query: ({ rewardId, payload }) => ({
        url: `/admin/user-reward/${rewardId}`,
        body: payload,
        method: 'PUT',
      }),
    }),
    bulkCreateRewards: builder.mutation<string[], RewardModelWithoutId[]>({
      query: (payload) => ({
        url: '/admin/user-reward/bulk',
        body: payload,
        method: 'POST',
      }),
    }),
    bulkUpdateRewards: builder.mutation<string[], RewardModel[]>({
      query: (payload) => ({
        url: '/admin/user-reward/bulk',
        body: payload,
        method: 'PUT',
      }),
    }),
    bulkDeleteRewards: builder.mutation<void, string[]>({
      query: (rewardIds) => ({
        url: '/admin/user-reward/bulk',
        params: {
          rewardIds,
        },
        method: 'DELETE',
      }),
    }),
    getRewardsByIds: builder.query<RewardModel[], string[]>({
      query: (rewardIds) => ({
        url: '/admin/user-reward/ids',
        params: {
          rewardIds,
        },
        method: 'GET',
      }),
    }),
    getTournaments: builder.query<PaginationResponse<AggregatedTournamentModel>, GetTournamentsArgs>({
      query: ({ page, limit, filter, sortFields }) => ({
        url: '/tournament',
        params: {
          page,
          limit,
          filter,
          sortFields: JSON.stringify(sortFields),
        },
        method: 'GET',
      }),
    }),
    getReferralRewardSettings: builder.query<ReferralRewardSettingsModel, void>({
      query: () => ({
        url: '/v1/admin/referral-reward-settings',
        method: 'GET',
      }),
    }),
    updateReferralRewardSettings: builder.mutation<ReferralRewardSettingsModel, ReferralRewardSettingsUpdateArgs>({
      query: (payload) => ({
        url: '/v1/admin/referral-reward-settings',
        method: 'PUT',
        body: payload,
      }),
    }),
    updateReferralUserRewardChance: builder.mutation<
      ReferralRewardUserProgressModel,
      UpdateReferralUserRewardChanceArgs
    >({
      query: ({ payload, userId }) => ({
        url: `/v1/admin/referral-reward-user-progress/personal-chance/${userId}`,
        method: 'PATCH',
        body: payload,
      }),
    }),
    getReferralUserRewardProgress: builder.query<ReferralRewardUserProgressModel, string>({
      query: (userId) => ({
        url: `/v1/admin/referral-reward-user-progress/user-config/${userId}`,
        method: 'GET',
      }),
    }),
    markReferralRewardAsReceived: builder.mutation<void, string>({
      query: (userId) => ({
        url: `/admin/user-reward/referral-reward/user/${userId}`,
        method: 'PATCH',
      }),
    }),
    assignReferral: builder.mutation<void, AssignUserArgs>({
      query: (body) => ({
        url: `/v1/admin/user/referral-assign`,
        method: 'PATCH',
        body,
      }),
    }),
    exportReferralSystemUsersProgress: builder.query<Blob, void>({
      query: () => ({
        url: `/v1/admin/referral-reward-user-progress/export`,
        responseHandler: async (response) => {
          const blob = await response.blob();
          const disposition = response.headers.get('content-disposition');
          const fileName = getFileNameFromDispositionHeader(disposition, 'Referral system users progress');

          await exportFile(blob, fileName);
        },
        method: 'GET',
        cache: 'no-cache',
      }),
    }),
    getMasterKeyById: builder.query<MasterKeyModel, string>({
      query: (id) => ({
        url: `/admin/master-key/${id}`,
        method: 'GET',
      }),
    }),
    getAllBetEvents: builder.query<BetEventModel[], void>({
      query: () => ({
        url: '/v1/admin/bet-event',
        method: 'GET',
      }),
    }),
    exportTopUserWinStreaks: builder.query<Blob, ExportTopBetsWinStreaksArgs>({
      query: ({ startDate, endDate, limit }) => ({
        url: `/v1/admin/bet-event/win-streak/export`,
        params: {
          startDate,
          endDate,
          limit,
        },
        responseHandler: async (response) => {
          const blob = await response.blob();
          const disposition = response.headers.get('content-disposition');
          const fileName = getFileNameFromDispositionHeader(disposition, 'Win streaks');

          await exportFile(blob, fileName);
        },
        method: 'GET',
        cache: 'no-cache',
      }),
    }),
    setIsActiveBetEvent: builder.mutation<BetEventModel, SetIsActiveBetEventArgs>({
      query: ({ payload, betEventId }) => ({
        url: `/v1/admin/bet-event/${betEventId}/active`,
        body: payload,
        method: 'PATCH',
      }),
    }),
    setBetEventStatus: builder.mutation<BetEventModel, SetBetEventStatusArgs>({
      query: ({ betEventId, payload }) => ({
        url: `/v1/admin/bet-event/${betEventId}/status`,
        body: payload,
        method: 'PATCH',
      }),
    }),
    updateBetEventsOrder: builder.mutation<BetEventModel, UpdateBetEventsOrderArgs>({
      query: (payload) => ({
        url: '/v1/admin/bet-event/order',
        body: payload,
        method: 'PATCH',
      }),
    }),
    finishBetEvent: builder.mutation<FinishBetEventResponse, FinishBetEventArgs>({
      query: ({ payload, betEventId }) => ({
        url: `/v1/admin/bet-event/${betEventId}/finish`,
        body: payload,
        method: 'PATCH',
      }),
    }),
    unfinishBetEvent: builder.mutation<UnfinishBetEventResponse, string>({
      query: (betEventId) => ({
        url: `/v1/admin/bet-event/${betEventId}/unfinish`,
        method: 'PATCH',
      }),
    }),
    createBetEvent: builder.mutation<BetEventModel, Omit<BetEventModel, 'status'>>({
      query: (payload) => ({
        url: '/v1/admin/bet-event',
        body: payload,
        method: 'POST',
        prepareHeaders: (headers: Headers) => {
          headers.set('Content-Type', 'multipart/form-data');

          return headers;
        },
      }),
    }),
    updateBetEvent: builder.mutation<BetEventModel, UpdateBetEventArgs>({
      query: ({ betEventId, payload }) => ({
        url: `/v1/admin/bet-event/${betEventId}`,
        body: payload,
        method: 'PUT',
        prepareHeaders: (headers: Headers) => {
          headers.set('Content-Type', 'multipart/form-data');

          return headers;
        },
      }),
    }),
    deleteBetEvent: builder.mutation<BetEventModel, string>({
      query: (betEventId) => ({
        url: `/v1/admin/bet-event/${betEventId}`,
        method: 'DELETE',
      }),
    }),
    deleteBetEventOutcome: builder.mutation<BetOutcomeModel, DeleteBetEventOutcomeArgs>({
      query: ({ betOutcomeId, betEventId }) => ({
        url: `/v1/admin/bet-event/${betEventId}/outcome/${betOutcomeId}`,
        method: 'DELETE',
      }),
    }),
    createBetOutcome: builder.mutation<BetOutcomeModel, Omit<BetOutcomeModel, 'bettors' | 'resultType'>>({
      query: (payload) => ({
        url: '/v1/admin/bet-outcome',
        body: payload,
        method: 'POST',
      }),
    }),
    updateBetOutcome: builder.mutation<BetOutcomeModel, UpdateBetOutcomeArgs>({
      query: ({ betOutcomeId, payload }) => ({
        url: `/v1/admin/bet-outcome/${betOutcomeId}`,
        body: payload,
        method: 'PUT',
      }),
    }),
    getBetOutcomes: builder.query<BetOutcomeModel[], string>({
      query: (betEventId) => ({
        url: `/v1/admin/bet-outcome/event/${betEventId}`,
        method: 'GET',
      }),
    }),
    exportOutcomeBettors: builder.query<Blob, ExportBetOutcomeArgs>({
      query: ({ betOutcomeId, betEventName }) => ({
        url: `/v1/admin/bet-outcome/${betOutcomeId}/export`,
        responseHandler: async (response) => {
          const blob = await response.blob();

          await exportFile(blob, `bettors_${betEventName}`);
        },
        method: 'GET',
        cache: 'no-cache',
      }),
    }),
    getProviderSlotsByTournamentId: builder.query<SlotModel[], string>({
      query: (tournamentId) => ({
        url: `/v1/admin/provider-slot/tournament/${tournamentId}`,
        method: 'GET',
      }),
    }),
    getAllVipTasks: builder.query<VipTaskModel[], void>({
      query: () => ({
        url: '/v1/admin/vip/task',
        method: 'GET',
      }),
    }),
    createVipTask: builder.mutation<VipTaskModel, Omit<VipTaskModel, 'id' | 'order'>>({
      query: (payload) => ({
        url: '/v1/admin/vip/task',
        body: payload,
        method: 'POST',
        prepareHeaders: (headers: Headers) => {
          headers.set('Content-Type', 'multipart/form-data');

          return headers;
        },
      }),
    }),
    updateVipTask: builder.mutation<VipTaskModel, UpdateVipTaskArgs>({
      query: ({ vipTaskId, payload }) => ({
        url: `/v1/admin/vip/task/${vipTaskId}`,
        body: payload,
        method: 'PUT',
        prepareHeaders: (headers: Headers) => {
          headers.set('Content-Type', 'multipart/form-data');

          return headers;
        },
      }),
    }),
    updateVipTaskOrder: builder.mutation<VipTaskModel[], UpdateVipTasksOrderArgs>({
      query: (payload) => ({
        url: '/v1/admin/vip/task/order',
        method: 'PATCH',
        body: payload,
      }),
    }),
    deleteVipTask: builder.mutation<VipTaskModel, string>({
      query: (vipTaskId) => ({
        url: `/v1/admin/vip/task/${vipTaskId}`,
        method: 'DELETE',
      }),
    }),
    getAllVipTaskItems: builder.query<VipTaskItemModel[], void>({
      query: () => ({
        url: '/v1/admin/vip/task/item',
        method: 'GET',
      }),
    }),
    getVipTaskItemsByTaskId: builder.query<VipTaskItemModel[], string>({
      query: (vipTaskId) => ({
        url: `/v1/admin/vip/task/item/task/${vipTaskId}`,
        method: 'GET',
      }),
    }),
    getVipTaskItemsByTaskIdAndUserId: builder.query<VipTaskUserItemModel[], GetVipTaskItemsParams>({
      query: ({ vipTaskId, userId }) => ({
        url: `/v1/admin/vip/task/item/task/${vipTaskId}/user/${userId}`,
        method: 'GET',
      }),
    }),
    createVipTaskItem: builder.mutation<VipTaskItemModel, Omit<VipTaskItemModel, 'id' | 'order' | 'status'>>({
      query: (payload) => ({
        url: '/v1/admin/vip/task/item',
        body: payload,
        method: 'POST',
        prepareHeaders: (headers: Headers) => {
          headers.set('Content-Type', 'multipart/form-data');

          return headers;
        },
      }),
    }),
    updateVipTaskItem: builder.mutation<VipTaskItemModel, UpdateVipTaskItemArgs>({
      query: ({ vipTaskItemId, payload }) => ({
        url: `/v1/admin/vip/task/item/${vipTaskItemId}`,
        body: payload,
        method: 'PUT',
        prepareHeaders: (headers: Headers) => {
          headers.set('Content-Type', 'multipart/form-data');

          return headers;
        },
      }),
    }),
    deleteVipTaskItem: builder.mutation<VipTaskItemModel, string>({
      query: (vipTaskItemId) => ({
        url: `/v1/admin/vip/task/item/${vipTaskItemId}`,
        method: 'DELETE',
      }),
    }),
    exportVipTaskItemParticipants: builder.query<Blob, string>({
      query: (vipTaskItemId) => ({
        url: `/v1/admin/vip/task/item/progress/${vipTaskItemId}/export`,
        responseHandler: async (response) => {
          const blob = await response.blob();
          const disposition = response.headers.get('content-disposition');
          const fileName = getFileNameFromDispositionHeader(disposition, 'participants');

          await exportFile(blob, fileName);
        },
        method: 'GET',
        cache: 'no-cache',
      }),
    }),
    validateVipTaskItem: builder.mutation<void, ValidateVipTaskItemArgs>({
      query: ({ vipTaskItemId, documentId }) => ({
        url: `/v1/admin/vip/task/item/${vipTaskItemId}/validation`,
        method: 'PATCH',
        body: { validationDocumentId: documentId },
      }),
    }),
    doneVipTaskItem: builder.mutation<void, DoneVipTaskItemArgs>({
      query: ({ vipTaskItemId, userId }) => ({
        url: `/v1/admin/vip/task/item/${vipTaskItemId}/done`,
        method: 'PATCH',
        body: { userId },
      }),
    }),
    getAllVipLevels: builder.query<VipLevelModel[], void>({
      query: () => ({
        url: '/v1/admin/vip/level',
        method: 'GET',
      }),
    }),
    createVipLevel: builder.mutation<VipLevelModel, Omit<VipLevelModel, 'id'>>({
      query: (payload) => ({
        url: '/v1/admin/vip/level',
        body: payload,
        method: 'POST',
        prepareHeaders: (headers: Headers) => {
          headers.set('Content-Type', 'multipart/form-data');

          return headers;
        },
      }),
    }),
    updateVipLevel: builder.mutation<VipLevelModel, UpdateVipLevelArgs>({
      query: ({ vipLevelId, payload }) => ({
        url: `/v1/admin/vip/level/${vipLevelId}`,
        body: payload,
        method: 'PUT',
        prepareHeaders: (headers: Headers) => {
          headers.set('Content-Type', 'multipart/form-data');

          return headers;
        },
      }),
    }),
    deleteVipLevel: builder.mutation<VipLevelModel, string>({
      query: (vipLevelId) => ({
        url: `/v1/admin/vip/level/${vipLevelId}`,
        method: 'DELETE',
      }),
    }),
    getVipLevelRewardsByLevelId: builder.query<VipLevelRewardModel[], string>({
      query: (vipLevelId) => ({
        url: `/v1/admin/vip/level/reward/level/${vipLevelId}`,
        method: 'GET',
      }),
    }),
    createVipLevelReward: builder.mutation<VipLevelRewardModel, Omit<VipLevelRewardModel, 'id' | 'order'>>({
      query: (payload) => ({
        url: '/v1/admin/vip/level/reward',
        body: payload,
        method: 'POST',
        prepareHeaders: (headers: Headers) => {
          headers.set('Content-Type', 'multipart/form-data');

          return headers;
        },
      }),
    }),
    updateVipLevelRewardsOrder: builder.mutation<VipLevelRewardModel[], UpdateVipLevelRewardsOrderArgs>({
      query: (payload) => ({
        url: '/v1/admin/vip/level/reward/order',
        method: 'PATCH',
        body: payload,
      }),
    }),
    updateVipLevelReward: builder.mutation<VipLevelRewardModel, UpdateVipLevelRewardArgs>({
      query: ({ vipLevelRewardId, payload }) => ({
        url: `/v1/admin/vip/level/reward/${vipLevelRewardId}`,
        body: payload,
        method: 'PUT',
        prepareHeaders: (headers: Headers) => {
          headers.set('Content-Type', 'multipart/form-data');

          return headers;
        },
      }),
    }),
    deleteVipLevelReward: builder.mutation<VipLevelRewardModel, string>({
      query: (vipLevelRewardId) => ({
        url: `/v1/admin/vip/level/reward/${vipLevelRewardId}`,
        method: 'DELETE',
      }),
    }),
    getVipWheelSettings: builder.query<VipWheelSettingsModel, void>({
      query: () => ({
        url: '/v1/admin/vip/wheel-settings',
        method: 'GET',
      }),
    }),
    updateVipWheelSettings: builder.mutation<VipWheelSettingsModel, Omit<VipWheelSettingsModel, 'id'>>({
      query: (payload) => ({
        url: '/v1/admin/vip/wheel-settings',
        method: 'PUT',
        body: payload,
      }),
    }),
    getUserProgresses: builder.query<
      PaginationResponse<ReferralRewardUserProgressModel>,
      PaginationArgs<ReferralRewardUserProgressModel>
    >({
      query: ({ page, limit, filter, sortFields }) => ({
        url: '/v1/admin/referral-reward-user-progress',
        params: {
          page,
          limit,
          filter,
          sortFields: JSON.stringify(sortFields),
        },
        method: 'GET',
      }),
    }),
    updateTelegramChatStatus: builder.mutation<void, UpdateTelegramChatStatusArgs>({
      query: ({ userId, isChatMessagesRead }) => ({
        url: `/v1/admin/telegram-chat/user/${userId}/chat/status`,
        method: 'PATCH',
        body: {
          isChatMessagesRead,
        },
      }),
    }),
    getOrderedAllTelegramChats: builder.query<PaginationResponse<TelegramChatModel>, PaginationArgs<TelegramChatModel>>(
      {
        query: ({ page, limit, filter, sortFields }) => ({
          url: '/v1/admin/telegram-chat',
          params: {
            page,
            limit,
            filter,
            sortFields: JSON.stringify(sortFields),
          },
          method: 'GET',
        }),
      }
    ),
    updateTelegramStatus: builder.mutation<TelegramChatModel, UpdateTelegramStatusArgs>({
      query: ({ telegramChatId, payload }) => ({
        url: `/v1/admin/telegram-chat/${telegramChatId}/status`,
        method: 'PATCH',
        body: payload,
      }),
    }),
    sentAdminMessage: builder.mutation<void, SendAdminMessageArgs>({
      query: (payload) => ({
        url: `/v1/admin/telegram-chat/message`,
        body: payload,
        method: 'POST',
      }),
    }),
    getTelegramChatMessages: builder.query<TelegramChatMessageModel[], string>({
      query: (userId) => ({
        url: `/v1/admin/telegram-chat/message/${userId}`,
        method: 'GET',
      }),
    }),
    getAllTickets: builder.query<TicketModel[], void>({
      query: () => ({
        url: '/v1/admin/ticket',
        method: 'GET',
      }),
    }),
    getOrderedTickets: builder.query<PaginationResponse<TicketModel>, PaginationArgs<TicketModel>>({
      query: ({ page, limit, filter }) => ({
        url: '/v1/admin/ticket/ordered',
        params: {
          page,
          limit,
          filter,
        },
        method: 'GET',
      }),
    }),
    createTicket: builder.mutation<TicketModel, Omit<TicketModel, 'id' | 'serialNumber' | 'status'>>({
      query: (payload) => ({
        url: '/v1/admin/ticket',
        body: payload,
        method: 'POST',
      }),
    }),
    deleteTicketById: builder.mutation<void, string>({
      query: (ticketId) => ({
        url: `/v1/admin/ticket/${ticketId}`,
        method: 'DELETE',
      }),
    }),
    expireTicketByIds: builder.mutation<void, string[]>({
      query: (ticketIds) => ({
        url: `/v1/admin/ticket/expire`,
        body: {
          ticketIds,
        },
        method: 'PATCH',
      }),
    }),
    updateTicketById: builder.mutation<TicketModel, UpdateTicketArgs>({
      query: ({ ticketId, payload }) => ({
        url: `/v1/admin/ticket/${ticketId}`,
        body: payload,
        method: 'PUT',
      }),
    }),
    expireTicketsByIds: builder.mutation<void, string[]>({
      query: (ticketIds) => ({
        url: `/v1/admin/ticket/expire`,
        method: 'PATCH',
        body: {
          ticketIds,
        },
      }),
    }),
    setContestTicketDefaultById: builder.mutation<TicketModel, SetDefaultContestTicketArgs>({
      query: ({ ticketId, isDefault }) => ({
        url: `/v1/admin/ticket/${ticketId}/default`,
        method: 'PATCH',
        body: {
          isDefault,
        },
      }),
    }),
  }),
});

export const {
  useUpdateBetEventsOrderMutation,
  useGetAllBetEventsQuery,
  useLazyExportOutcomeBettorsQuery,
  useDeleteBetEventOutcomeMutation,
  useGetBetOutcomesQuery,
  useDeleteBetEventMutation,
  useSetIsActiveBetEventMutation,
  useSetBetEventStatusMutation,
  useFinishBetEventMutation,
  useUnfinishBetEventMutation,
  useUpdateBetOutcomeMutation,
  useCreateBetOutcomeMutation,
  useUpdateBetEventMutation,
  useCreateBetEventMutation,
  useLazyGetMasterKeyByIdQuery,
  useGetProviderSlotsByTournamentIdQuery,
  useBulkUpdateRewardsMutation,
  useResetDailyRewardVisitsMutation,
  useResetCompletedDailyTasksMutation,
  useResetSpecialDropDatesMutation,
  useResetReceivedRewardsMutation,
  useBulkDeleteRewardsMutation,
  useLazyGetRewardsByIdsQuery,
  useBulkCreateRewardsMutation,
  useCreateRewardMutation,
  useUpdateRewardMutation,
  useLazyGetUsersQuery,
  useLazyGetUsersByNicknameQuery,
  useLazyGetUserByIdQuery,
  useGetTournamentsQuery,
  useGetAllVipTasksQuery,
  useCreateVipTaskMutation,
  useUpdateVipTaskMutation,
  useUpdateVipTaskOrderMutation,
  useDeleteVipTaskMutation,
  useGetVipTaskItemsByTaskIdQuery,
  useCreateVipTaskItemMutation,
  useUpdateVipTaskItemMutation,
  useDeleteVipTaskItemMutation,
  useLazyExportVipTaskItemParticipantsQuery,
  useValidateVipTaskItemMutation,
  useDoneVipTaskItemMutation,
  useGetVipTaskItemsByTaskIdAndUserIdQuery,
  useGetAllVipLevelsQuery,
  useCreateVipLevelMutation,
  useUpdateVipLevelMutation,
  useDeleteVipLevelMutation,
  useGetVipLevelRewardsByLevelIdQuery,
  useCreateVipLevelRewardMutation,
  useUpdateVipLevelRewardMutation,
  useUpdateVipLevelRewardsOrderMutation,
  useDeleteVipLevelRewardMutation,
  useGetVipWheelSettingsQuery,
  useUpdateVipWheelSettingsMutation,
  useGetReferralRewardSettingsQuery,
  useUpdateReferralRewardSettingsMutation,
  useUpdateReferralUserRewardChanceMutation,
  useLazyGetReferralUserRewardProgressQuery,
  useMarkReferralRewardAsReceivedMutation,
  useLazyExportReferralSystemUsersProgressQuery,
  useLazyGetUserProgressesQuery,
  useUpdateTelegramChatStatusMutation,
  useUpdateTelegramStatusMutation,
  useLazyGetOrderedAllTelegramChatsQuery,
  useGetTelegramChatMessagesQuery,
  useSentAdminMessageMutation,
  useAssignReferralMutation,
  useLazyExportTopUserWinStreaksQuery,
  useGetAllTicketsQuery,
  useLazyGetOrderedTicketsQuery,
  useCreateTicketMutation,
  useDeleteTicketByIdMutation,
  useExpireTicketByIdsMutation,
  useUpdateTicketByIdMutation,
  useSetContestTicketDefaultByIdMutation,
} = dataApi;
